/**
 * SystemIncidents — Alertmanager / Glitchtip / internal feed.
 *
 * Lists the most recent unacknowledged incidents. Severity colour
 * survives only on the leading dot (status palette). Chrome stays
 * monochrome.
 */
import { ExternalLink } from 'lucide-react';

import { cn } from '../../lib/utils';
import type { IncidentRow, IncidentsResponse } from './dashboard-types';

const SEVERITY_DOT: Record<IncidentRow['severity'], string> = {
  info: 'bg-foreground/40',
  warning: 'bg-amber-500',
  error: 'bg-rose-500',
  critical: 'bg-rose-600',
};

export interface SystemIncidentsProps {
  data: IncidentsResponse | undefined;
  isLoading?: boolean;
  className?: string;
}

export function SystemIncidents({ data, isLoading, className }: SystemIncidentsProps) {
  const incidents = data?.incidents ?? [];
  return (
    <section
      aria-labelledby="incidents-heading"
      className={cn('rounded-md border border-border bg-background p-4', className)}
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h2 id="incidents-heading" className="text-sm font-semibold text-foreground">
          System incidents
        </h2>
        <span className="text-2xs text-muted-foreground">{incidents.length} open</span>
      </header>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : incidents.length === 0 ? (
        <p className="rounded border border-dashed border-border bg-foreground/[0.02] p-4 text-center text-xs text-muted-foreground">
          All systems normal.
        </p>
      ) : (
        <ul className="divide-y divide-border text-xs">
          {incidents.map((i) => (
            <li key={i.id} className="flex items-center gap-2 py-2">
              <span
                aria-hidden
                className={cn('h-2 w-2 shrink-0 rounded-full', SEVERITY_DOT[i.severity])}
              />
              <span className="flex-1 truncate text-foreground" title={i.title}>
                {i.title}
              </span>
              <span className="text-2xs text-muted-foreground">
                {i.affected_service ?? 'system'}
              </span>
              {i.url && (
                <a
                  href={i.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={`Open ${i.title}`}
                >
                  <ExternalLink size={11} aria-hidden />
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
